import { useCallback, useEffect, useRef, useState } from "react";

// Un champ où l'on SIGNE AU DOIGT (téléphone, tablette) ou à la souris.
//
// POURQUOI UN COMPOSANT À PART, ET PAS UNE BIBLIOTHÈQUE
// Une signature, c'est un canevas et des évènements de pointeur : une
// cinquantaine de lignes. Ajouter une dépendance pour cela, c'est une chose de
// plus à tenir à jour pour un besoin qui ne bougera pas.
//
// LES ÉVÈNEMENTS DE POINTEUR, PAS LA SOURIS NI LE TACTILE
// `pointerdown/move/up` traitent le doigt, le stylet et la souris de la même
// façon : un seul code, sans deux chemins qui finiraient par diverger. Et
// `touch-action: none` (voir App.css) : sans lui, le navigateur d'un téléphone
// prend le mouvement du doigt pour un défilement de la page, et la signature
// ne se trace jamais.
//
// UN BOUTON « ENREGISTRER », PAS UN ENVOI À CHAQUE TRAIT
// Une signature se fait en plusieurs traits (on lève le doigt). Envoyer à
// chaque levée ferait autant d'envois, dont tous sauf le dernier remplacés.
// Le geste est donc explicite : on trace, on relit, on enregistre.
//
// L'ENCRE EST SOMBRE ET FIXE, ET C'EST UNE EXCEPTION À « AUCUNE COULEUR EN
// DUR » : la signature atterrit sur le PDF du contrat, qui est BLANC quelle que
// soit la charte du client. Une encre prise dans la couleur du client serait
// invisible sur une charte claire — le défaut de contraste de 1,10 pour 1 déjà
// payé le 19/08, transposé à un document juridique.
const ENCRE = "#111827";
const EPAISSEUR = 2.5;

export default function ChampSignature({
  libelle,
  posee,
  disabled = false,
  onEnregistrer,
  // Le geste change de nom selon l'usage : « Enregistrer la signature » pour un
  // contrat qui se signe en plusieurs temps, « Signer l'avenant » quand
  // l'enregistrement EST l'envoi de tout l'avenant.
  libelleBouton = "Enregistrer la signature",
  libelleRemplacer = "Remplacer la signature",
}) {
  const canevas = useRef(null);
  const trace = useRef({ actif: false, x: 0, y: 0 });
  const [dessine, setDessine] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");

  // La taille INTERNE du canevas suit sa taille affichée (× densité de pixels,
  // plafonnée à 2) : sans cela le trait est flou sur un écran de téléphone.
  //
  // ⚠️ REDIMENSIONNER UN CANEVAS L'EFFACE. Le navigateur d'un téléphone déclenche
  // « resize » quand la barre d'adresse se masque en défilant, quand le clavier
  // s'ouvre, à la rotation : la signature disparaissait en cours de tracé alors
  // que le bouton « Enregistrer » restait actif — on aurait enregistré une
  // signature BLANCHE. Trouvé sur une capture d'écran, pas par un test serveur.
  // Deux parades : on ne touche à rien si la LARGEUR n'a pas changé (la barre
  // d'adresse ne change que la hauteur), et sinon on REDESSINE l'ancien tracé.
  const largeur = useRef(0);
  const dimensionner = useCallback(() => {
    const c = canevas.current;
    if (!c) return;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const { width, height } = c.getBoundingClientRect();
    if (!width || !height) return;
    if (Math.round(width) === largeur.current) return;
    largeur.current = Math.round(width);

    let ancien = null;
    if (c.width && c.height) {
      ancien = document.createElement("canvas");
      ancien.width = c.width;
      ancien.height = c.height;
      ancien.getContext("2d").drawImage(c, 0, 0);
    }
    c.width = Math.round(width * ratio);
    c.height = Math.round(height * ratio);
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    if (ancien) ctx.drawImage(ancien, 0, 0, width, height);
    ctx.lineWidth = EPAISSEUR;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = ENCRE;
  }, []);

  useEffect(() => {
    dimensionner();
    window.addEventListener("resize", dimensionner);
    return () => window.removeEventListener("resize", dimensionner);
  }, [dimensionner]);

  const point = (e) => {
    const r = canevas.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const commencer = (e) => {
    if (disabled) return;
    // Le pointeur reste capturé tant que le doigt est posé : le trait ne se
    // coupe pas quand il sort un instant du cadre.
    canevas.current.setPointerCapture(e.pointerId);
    const p = point(e);
    trace.current = { actif: true, x: p.x, y: p.y };
    const ctx = canevas.current.getContext("2d");
    // Un simple appui laisse un point : une signature peut en comporter.
    ctx.beginPath();
    ctx.arc(p.x, p.y, EPAISSEUR / 2, 0, Math.PI * 2);
    ctx.fillStyle = ENCRE;
    ctx.fill();
    setDessine(true);
  };

  const tracer = (e) => {
    if (!trace.current.actif) return;
    const p = point(e);
    const ctx = canevas.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(trace.current.x, trace.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    trace.current = { actif: true, x: p.x, y: p.y };
  };

  const finir = () => {
    trace.current.actif = false;
  };

  const effacer = () => {
    const c = canevas.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setDessine(false);
    setErreur("");
  };

  // Le canevas porte-t-il un trait ? Le bouton s'active à l'appui du doigt, pas
  // au trait : cette vérification, sur les PIXELS, est la seule qui garantisse
  // qu'on n'enregistre jamais une signature blanche.
  const porteUnTrait = () => {
    const c = canevas.current;
    const donnees = c.getContext("2d").getImageData(0, 0, c.width, c.height).data;
    for (let i = 3; i < donnees.length; i += 4) if (donnees[i] > 0) return true;
    return false;
  };

  const enregistrer = async () => {
    setErreur("");
    if (!porteUnTrait()) {
      setDessine(false);
      return setErreur("Tracez la signature avant de l'enregistrer.");
    }
    setEnvoi(true);
    try {
      const blob = await new Promise((resolve, reject) =>
        canevas.current.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("vide"))),
          "image/png"
        )
      );
      await onEnregistrer(blob);
      effacer();
    } catch (err) {
      setErreur(err?.message || "Enregistrement de la signature impossible.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="champ-signature">
      <div className="champ-signature-entete">
        <span className="champ-label">{libelle}</span>
        {posee && <span className="signature-posee">✓ Signature enregistrée</span>}
      </div>
      <canvas
        ref={canevas}
        className="signature-canevas"
        role="img"
        aria-label={`Zone de signature : ${libelle}`}
        onPointerDown={commencer}
        onPointerMove={tracer}
        onPointerUp={finir}
        onPointerCancel={finir}
      />
      {erreur && <p className="erreur">{erreur}</p>}
      {!disabled && (
        <div className="actions-groupe">
          <button type="button" className="btn" onClick={effacer} disabled={!dessine || envoi}>
            Effacer
          </button>
          <button
            type="button"
            className="btn-accent"
            onClick={enregistrer}
            disabled={!dessine || envoi}
          >
            {envoi ? "Enregistrement…" : posee ? libelleRemplacer : libelleBouton}
          </button>
        </div>
      )}
    </div>
  );
}
